import {
  TableCell,
  TextField,
  Autocomplete,
  IconButton,
  Button,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { RemoveCircle } from "@mui/icons-material";
import SiteNameModal from "@pages/Order/components/SiteNameModal/SiteNameModal";
import { useState } from "react";
import { doseLabels } from "@utils/doseAndFractions";
import { DoseEditDialog } from "./DoseEditDialog";

interface BaseTableCellProps {
  isReadOnly: boolean;
  errors?: string[];
  field: string;
}

interface TextFieldCellProps extends BaseTableCellProps {
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
}

export const TextFieldCell = ({
  value,
  onChange,
  isReadOnly,
  errors,
  field,
  multiline,
}: TextFieldCellProps) => {
  const theme = useTheme();
  const hasErrors = errors?.includes(field);
  return (
    <TableCell
      sx={{
        padding: 0,
        paddingLeft: 1,
        verticalAlign: multiline ? "top" : "middle",
        backgroundColor: hasErrors ? theme.palette.error.light : "none",
        borderLeft: hasErrors
          ? `1px solid ${theme.palette.error.main}`
          : "default",
        borderRight: hasErrors
          ? `1px solid ${theme.palette.error.main}`
          : "default",
      }}
    >
      <TextField
        value={value}
        disabled={isReadOnly}
        onChange={(e) => onChange(e.target.value)}
        error={hasErrors}
        size="small"
        fullWidth
        multiline={multiline}
        variant="standard"
        sx={{
          "& .MuiInputBase-root": {
            padding: 0,
            "&:before, &:after": {
              display: "none",
            },
          },
          "& .MuiInputBase-input": {
            padding: 1,
            paddingLeft: 0,
            wordWrap: "break-word",
            fontSize: "14px",
          },
          "& .MuiInputBase-inputMultiline": {
            padding: 1,
            paddingLeft: 0,
            wordWrap: "break-word",
            fontSize: "14px",
          },
        }}
      />
    </TableCell>
  );
};

interface AutocompleteCellProps extends BaseTableCellProps {
  value: string;
  options: string[];
  onChange: (value: string) => void;
}

export const AutocompleteCell = ({
  value,
  options,
  onChange,
  isReadOnly,
  errors,
  field,
}: AutocompleteCellProps) => {
  const theme = useTheme();
  const hasErrors = errors?.includes(field);
  return (
    <TableCell
      sx={{
        padding: 0,
        paddingLeft: 1,
        backgroundColor: hasErrors ? theme.palette.error.light : "none",
        borderLeft: hasErrors
          ? `1px solid ${theme.palette.error.main}`
          : "default",
        borderRight: hasErrors
          ? `1px solid ${theme.palette.error.main}`
          : "default",
      }}
    >
      <Autocomplete
        sx={{
          height: 1,
          width: 1,
        }}
        forcePopupIcon={false}
        value={value}
        options={options}
        disabled={isReadOnly}
        onChange={(_, value) => onChange(value || "")}
        disableClearable
        renderInput={(params) => (
          <TextField
            {...params}
            error={hasErrors}
            size="small"
            fullWidth
            multiline
            variant="standard"
            sx={{
              "& .MuiInputBase-root": {
                padding: 0,
                "&:before, &:after": {
                  display: "none",
                },
              },
              "& .MuiInputBase-input": {
                padding: 1,
                whiteSpace: "normal",
                wordWrap: "break-word",
                fontSize: "14px",
              },
            }}
          />
        )}
        slotProps={{
          popper: {
            placement: "bottom-start",
            style: {
              width: "unset",
            },
          },
        }}
      />
    </TableCell>
  );
};

interface TreatmentSiteCellProps extends BaseTableCellProps {
  value: string;
  nameSpecify: string;
  options: string[];
  onUpdate: (name: string, nameSpecify: string) => void;
}

export const TreatmentSiteCell = ({
  value,
  nameSpecify,
  options,
  isReadOnly,
  errors,
  field,
  onUpdate,
}: TreatmentSiteCellProps) => {
  const theme = useTheme();
  const hasErrors = errors?.includes(field);
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <TableCell
      sx={{
        padding: 0,
        paddingLeft: 1,
        backgroundColor: hasErrors ? theme.palette.error.light : "none",
        borderLeft: hasErrors
          ? `1px solid ${theme.palette.error.main}`
          : "default",
        borderRight: hasErrors
          ? `1px solid ${theme.palette.error.main}`
          : "default",
        cursor: isReadOnly ? "default" : "auto",
        color: isReadOnly ? "text.disabled" : "inherit",
      }}
    >
      {isReadOnly ? (
        `${value} ${nameSpecify || ""}`.trim()
      ) : (
        <>
          <Button
            variant="text"
            onClick={() => setIsModalOpen(true)}
            sx={{
              textTransform: "none",
              justifyContent: "flex-start",
              fontWeight: "normal",
              width: "100%",
              textAlign: "left",
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            {`${value} ${nameSpecify || ""}`.trim() || "Select Treatment Site"}
          </Button>
        </>
      )}
      <SiteNameModal
        open={isModalOpen}
        options={options}
        onSubmit={(name, nameSpecify) => {
          onUpdate(name, nameSpecify);
          setIsModalOpen(false);
        }}
        onDismiss={() => setIsModalOpen(false)}
        editValues={{
          name: value || "",
          nameSpecify: nameSpecify || "",
        }}
      />
    </TableCell>
  );
};

interface ActionCellProps {
  onDelete: () => void;
}

export const ActionCell = ({ onDelete }: ActionCellProps) => {
  const theme = useTheme();
  return (
    <TableCell
      sx={{
        padding: 1,
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
      }}
    >
      <IconButton
        onClick={onDelete}
        size="small"
        aria-label="Delete"
        data-testid="delete-button"
      >
        <RemoveCircle />
      </IconButton>
    </TableCell>
  );
};

interface DoseCellProps extends BaseTableCellProps {
  value: string;
  onChange: (value: string) => void;
  doseUnit: string;
  doses: (number | null)[];
  onDosesChange: (doses: number[]) => void;
}

export const DoseCell = ({
  isReadOnly,
  errors,
  field,
  doseUnit,
  doses,
  onDosesChange,
}: DoseCellProps) => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const hasErrors = errors?.includes(field);

  const handleClick = () => {
    if (!isReadOnly && !openDialog) {
      setOpenDialog(true);
    }
  };

  const handleDialogSubmit = (newDoses: number[]) => {
    onDosesChange(newDoses);
    setOpenDialog(false);
  };

  return (
    <TableCell
      sx={{
        padding: 0,
        paddingLeft: 1,
        verticalAlign: "middle",
        cursor: isReadOnly ? "default" : "pointer",
        backgroundColor: hasErrors ? theme.palette.error.light : "none",
        borderLeft: hasErrors
          ? `1px solid ${theme.palette.error.main}`
          : "default",
        borderRight: hasErrors
          ? `1px solid ${theme.palette.error.main}`
          : "default",
        color: isReadOnly ? "text.disabled" : "inherit",
      }}
      onClick={handleClick}
    >
      {doses.length <= 1 ? (
        <Typography
          variant="body2"
          sx={{ display: "flex", alignItems: "center", height: "100%" }}
        >
          {doses[0]}
        </Typography>
      ) : (
        <Stack>
          {doses.map((dose, index) => (
            <Typography key={index} variant="body2">
              {doseLabels[doses.length][index]}: {dose}
            </Typography>
          ))}
        </Stack>
      )}
      <DoseEditDialog
        open={openDialog}
        setOpen={setOpenDialog}
        doseValues={doses.length === 0 ? [null] : doses}
        onSubmit={handleDialogSubmit}
        doseUnit={doseUnit}
      />
    </TableCell>
  );
};
